/**
 * Índice GSPS: series PR del estudio y mediciones por SOP referenciado.
 */
import { fileModality } from './petFusion'
import { gspsToMeasurements } from './gspsMap'
import { isGspsDataset, parseGspsFromBytes, parseGspsFromImage } from './gspsParse'

export function isPrModality(mod) {
  return String(mod || '').toUpperCase() === 'PR'
}

export function seriesLooksPr(files) {
  if (!files || !files.length) return false
  const sample = files.find(Boolean)
  return isPrModality(fileModality(sample))
}

/**
 * @param {{ seriesList?: Map, seriesKeys?: any[] }|null} seriesStore
 * @returns {Array<{ key: *, files: object[], modality: string, label: string }>}
 */
export function listPrSeriesFromStore(seriesStore) {
  if (!seriesStore || !seriesStore.seriesList) return []
  const keys = Array.isArray(seriesStore.seriesKeys)
    ? seriesStore.seriesKeys
    : [...seriesStore.seriesList.keys()]
  const out = []
  keys.forEach((key) => {
    const files = seriesStore.seriesList.get(key)
    if (!seriesLooksPr(files)) return
    const f = files.find(Boolean)
    const desc =
      (f && f.series && f.series.seriesDescription) ||
      (f && f.seriesDescription) ||
      'GSPS'
    out.push({
      key,
      files,
      modality: fileModality(f),
      label: `${desc} (${files.length})`,
    })
  })
  return out
}

export function flattenPrFiles(seriesStore) {
  const listed = listPrSeriesFromStore(seriesStore)
  const files = []
  listed.forEach((s) => {
    ;(s.files || []).forEach((f) => {
      if (f) files.push(f)
    })
  })
  return files
}

export function cloneMeasurement(measure) {
  if (!measure) return null
  try {
    return JSON.parse(JSON.stringify(measure))
  } catch (e) {
    return {
      tool: measure.tool,
      note: measure.note || '',
      data: measure.data,
    }
  }
}

/**
 * @param {Array<{ parsed: object, measurements?: object[] }>} entries
 * @returns {Object<string, object[]>}
 */
export function indexGspsByReferencedSop(entries) {
  const bySop = {}
  ;(entries || []).forEach((entry) => {
    const parsed = entry && entry.parsed
    if (!parsed || !parsed.isGsps) return
    const measures = entry.measurements || gspsToMeasurements(parsed)
    const uids = parsed.referencedSopUids || []
    uids.forEach((uid) => {
      if (!uid) return
      if (!bySop[uid]) bySop[uid] = []
      measures.forEach((m) => {
        const cloned = cloneMeasurement(m)
        if (cloned) {
          cloned.note = cloned.note || 'GSPS'
          if (cloned.data) cloned.data._fromGsps = true
          bySop[uid].push(cloned)
        }
      })
    })
  })
  return bySop
}

/**
 * Softcopy VOI (WW/WC) por SOP referenciado — último GSPS gana por SOP.
 * @param {Array<{ parsed: object }>} entries
 * @returns {Object<string, { windowWidth: number, windowCenter: number }>}
 */
export function indexGspsVoiByReferencedSop(entries) {
  const bySop = {}
  ;(entries || []).forEach((entry) => {
    const parsed = entry && entry.parsed
    if (!parsed || !parsed.isGsps) return
    const allRefs = parsed.referencedSopUids || []
    const voiList = parsed.softcopyVoi || []
    voiList.forEach((voi) => {
      if (!voi) return
      const ww = Number(voi.windowWidth)
      const wc = Number(voi.windowCenter)
      if (!(ww > 0) || !Number.isFinite(wc)) return
      const targets =
        voi.referencedSopUids && voi.referencedSopUids.length
          ? voi.referencedSopUids
          : allRefs
      targets.forEach((uid) => {
        if (!uid) return
        bySop[String(uid)] = { windowWidth: ww, windowCenter: wc }
      })
    })
  })
  return bySop
}

export function parsedFromImage(image) {
  if (!image || !image.data) return null
  if (!isGspsDataset(image.data)) return null
  return parseGspsFromImage(image)
}

export function toUint8(data) {
  if (!data) return null
  if (data instanceof Uint8Array) return data
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return new Uint8Array(data)
  }
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
  }
  return null
}

export function gspsSourceUrl(file) {
  if (!file) return ''
  if (file.dicomUrl) return String(file.dicomUrl)
  const id = String(file.imageId || '')
  if (id.indexOf('wadouri:') === 0) return id.slice('wadouri:'.length)
  if (/^https?:/i.test(id)) return id
  return ''
}

async function resolveGspsBytes(file) {
  if (!file) return null
  if (typeof Blob !== 'undefined' && typeof file.data === 'object' && file.data instanceof Blob) {
    try {
      return new Uint8Array(await file.data.arrayBuffer())
    } catch (e) {
      /* ignore */
    }
  }
  const local = toUint8(file.data || file.buffer)
  if (local) return local
  if (file.sopInstanceUID) {
    try {
      const { getCachedInstance } = await import('../db/dicomCache')
      const row = await getCachedInstance(String(file.sopInstanceUID))
      if (row && row.blob) {
        if (typeof Blob !== 'undefined' && row.blob instanceof Blob) {
          return new Uint8Array(await row.blob.arrayBuffer())
        }
        const u = toUint8(row.blob)
        if (u) return u
      }
    } catch (e) {
      /* ignore */
    }
  }
  const url = gspsSourceUrl(file)
  if (url && /^https?:/i.test(url)) {
    try {
      const res = await fetch(url)
      if (res.ok) return new Uint8Array(await res.arrayBuffer())
    } catch (e) {
      /* ignore */
    }
  }
  return null
}

/**
 * Parsea un archivo PR sin exigir pixel data (GSPS no es imagen).
 * @param {object} file
 */
export async function parseGspsFromFile(file) {
  const fromImage = parsedFromImage(file && file.image)
  if (fromImage && fromImage.isGsps) return fromImage
  const bytes = await resolveGspsBytes(file)
  if (!bytes) return null
  try {
    const parsed = parseGspsFromBytes(bytes)
    return parsed && parsed.isGsps ? parsed : null
  } catch (e) {
    return null
  }
}
